import { useState, useCallback, useEffect } from 'react';
import { LocalDataTrack } from 'twilio-video';

export const useChat = (room, user) => {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [localDataTrack, setLocalDataTrack] = useState(null);

  // Initialize local data track immediately (don't wait for room)
  useEffect(() => {
    if (!localDataTrack) {
      const newLocalDataTrack = new LocalDataTrack();
      setLocalDataTrack(newLocalDataTrack);
    }
  }, [localDataTrack]);

  // Publish data track when room becomes available
  useEffect(() => {
    if (room && localDataTrack) {
      // Publish the data track to the room
      room.localParticipant.publishTrack(localDataTrack);
    }
  }, [room, localDataTrack]);

  // Handle incoming data tracks from remote participants
  const handleDataTrack = useCallback((dataTrack, participant) => {
    console.log('Setting up data track for participant:', participant.identity);
    
    // Listen for messages from the data track
    dataTrack.on('message', (message) => {
      console.log('Received chat message:', message, 'from:', participant.identity);
      setChatMessages(prev => [
        ...prev,
        {
          sender: participant.identity || 'Remote',
          body: message,
          timestamp: new Date().toISOString(),
        },
      ]);
    });
  }, []);

  // Set up data track handling for existing and new participants
  useEffect(() => {
    if (!room) return;

    const setupDataTrackForParticipant = (participant) => {
      // Handle existing data tracks
      participant.tracks.forEach(publication => {
        if (publication.track && publication.track.kind === 'data') {
          handleDataTrack(publication.track, participant);
        }
      });

      // Handle new data tracks
      participant.on('trackPublished', publication => {
        if (publication.track && publication.track.kind === 'data') {
          handleDataTrack(publication.track, participant);
        }
      });

      participant.on('trackSubscribed', track => {
        if (track.kind === 'data') {
          handleDataTrack(track, participant);
        }
      });
    };

    // Setup for existing participants
    room.participants.forEach(setupDataTrackForParticipant);

    // Setup for new participants
    const handleParticipantConnected = (participant) => {
      setupDataTrackForParticipant(participant);
    };

    room.on('participantConnected', handleParticipantConnected);

    return () => {
      room.off('participantConnected', handleParticipantConnected);
    };
  }, [room, handleDataTrack]);

  // Send chat message
  const sendChatMessage = useCallback(() => {
    if (localDataTrack && chatInput.trim()) {
      localDataTrack.send(chatInput);
      setChatMessages(prev => [
        ...prev,
        {
          sender: user?.displayName || user?.email || 'You',
          body: chatInput,
          timestamp: new Date().toISOString(),
        },
      ]);
      setChatInput('');
    }
  }, [localDataTrack, chatInput, user]);

  // Handle Enter key press
  const handleChatKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      sendChatMessage();
    }
  }, [sendChatMessage]);

  // Clear chat messages
  const clearChatMessages = useCallback(() => {
    setChatMessages([]);
  }, []);

  return {
    chatMessages,
    chatInput,
    setChatInput,
    sendChatMessage,
    handleChatKeyPress,
    clearChatMessages,
    localDataTrack
  };
}; 